import { Capacitor } from "@capacitor/core";
import { isAndroidShell } from "./platform";
import { NativePurchases, PURCHASE_TYPE } from "@capgo/native-purchases";
import api from "./api";

export const PLAY_PRODUCT_ID = "sorted_standard_monthly";
export const PLAY_BASE_PLAN_ID = "monthly";
export const PLAY_MANAGE_URL = "https://play.google.com/store/account/subscriptions";

export const isAndroidApp = isAndroidShell;

// Opaque, non-PII account binding for Google (obfuscatedAccountId, ≤64 chars).
const accountToken = (userId) => `u_${String(userId).replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 60)}`;

// Returns the base plan's offers; prefers the free-trial offer when the user is eligible.
export async function loadPlayOffer() {
  if (!Capacitor.isNativePlatform()) throw new Error("Please update the Sorted app to subscribe through Google Play");
  const { isBillingSupported } = await NativePurchases.isBillingSupported();
  if (!isBillingSupported) throw new Error("Google Play Billing isn't available on this device");
  const { products } = await NativePurchases.getProducts({ productIdentifiers: [PLAY_PRODUCT_ID], productType: PURCHASE_TYPE.SUBS });
  const offers = products.filter((p) => p.identifier === PLAY_BASE_PLAN_ID || p.planIdentifier === PLAY_PRODUCT_ID);
  if (!offers.length) throw new Error("Subscription not found in Google Play yet");
  const trial = offers.find((p) => p.offerId);
  return { offer: trial || offers.find((p) => !p.offerId) || offers[0], hasTrial: !!trial };
}

export async function purchasePlay(userId, offer) {
  const tx = await NativePurchases.purchaseProduct({
    productIdentifier: PLAY_PRODUCT_ID,
    planIdentifier: PLAY_BASE_PLAN_ID,
    offerToken: offer?.offerToken,
    productType: PURCHASE_TYPE.SUBS,
    appAccountToken: accountToken(userId),
    autoAcknowledgePurchases: false,
  });
  if (!tx?.purchaseToken) throw new Error("Purchase didn't complete");
  return tx;
}

// Send a purchase token to the server for verification + acknowledgement.
export async function verifyPlayPurchase(tx) {
  const { data } = await api.post("/billing/google/verify", {
    product_id: PLAY_PRODUCT_ID, base_plan_id: PLAY_BASE_PLAN_ID, purchase_token: tx.purchaseToken, order_id: tx.transactionId || null,
  });
  return data;
}

// On launch: restore and re-verify any existing Play subscription for this account.
export async function restorePlayPurchases(userId) {
  if (!isAndroidApp()) return [];
  try {
    await NativePurchases.restorePurchases();
    const { purchases } = await NativePurchases.getPurchases({ productType: PURCHASE_TYPE.SUBS, appAccountToken: accountToken(userId), onlyCurrentEntitlements: true });
    const mine = purchases.filter((p) => p.productIdentifier === PLAY_PRODUCT_ID && p.purchaseToken);
    for (const tx of mine) await verifyPlayPurchase(tx).catch(() => {});
    return mine;
  } catch { return []; }
}
